import { registerErrorDataGatherer, reportException, SkywardException } from "#skyward_error";
import { Dictionary } from "./types";
// @ts-ignore This file is created by the Webpack build
import { lazy } from "./skyward.base.extensions.lazy";
// @ts-ignore
import { getCurrentScope, onScopeDispose } from 'vue';
import { loadModule } from "./skyward.webpack.loadModule.generated";

const skyward = window.skyward;
skyward.global = skyward.global || {};

const isSkySerialJsonpRequest = function (options: JQuery.AjaxSettings)
{
    if (options.dataType !== "jsonp" || !options.url)
    {
        return false;
    }

    const skySerialHost = $.skyserial?.defaults?.host;
    if (!skySerialHost)
    {
        return false;
    }

    try
    {
        return new URL(options.url, window.location.href).origin === new URL(skySerialHost, window.location.href).origin;
    }
    catch
    {
        return options.url.indexOf(skySerialHost) === 0;
    }
};

$.ajaxPrefilter(function (options, _originalOptions, jqXHR)
{
    // JQuery is fetching a viewpart-specific script: pass it through so that it can be cached across windows
    if (!options.global && options.dataType === "script" && options.type === "GET" && options.url!.indexOf("/Scripts/") !== -1)
    {
        return;
    }
    options.url = addRequiredQuerystringParams(options.url!);

    if (!isSkySerialJsonpRequest(options))
    {
        options.headers = options.headers || {};
        options.headers["X-CSRF-TOKEN"] = sessionGuidHash;
    }

    if (options.signal)
    {
        if (options.signal.aborted)
        {
            jqXHR.abort();
        }
        else
        {
            const abortHandler = () => jqXHR.abort();
            options.signal.addEventListener('abort', abortHandler);

            jqXHR.always(() =>
            {
                options.signal!.removeEventListener('abort', abortHandler);
            });
        }
    }

    if (options.useVueScope === true && getCurrentScope() !== undefined)
    {
        onScopeDispose(() =>
        {
            // Only abort if it hasn't finished yet
            if (jqXHR.readyState !== 4)
            {
                jqXHR.abort();
            }
        });
    }
});

//Public
export let calculateTextWidthPx = function (text: string)
{
    var mockStyle = "position:absolute; display:none;";

    // Make a div out of our text and check its width so we can determine how wide the textbox should be.
    var div = $('<div />', { 'style': mockStyle });
    div.text(text);
    $(document.body).append(div);
    var width = div.width()!;
    div.remove();

    return width;
};

export let validateDataParameter = function (data: any): Dictionary<string, any>
{
    if (!data)
    {
        return {};
    }
    if (typeof data === "object")
    {
        return data;
    }
    reportException(new SkywardException(`The "data" parameter should be an object or null/undefined. (Got a ${typeof data})`));
    return {};
};

/**
 * Create a form to POST values to a url.         
 * @param url The url that the form will POST to.
 * @param data name:value pairs to post with the form.
 * @param inNewWindow Should the form post to a new window (false by default).
 * @param isExternalLink Is this url outside of the application (false by default).
 * @returns Form element
 */
export let createHiddenInputForm = function (url: string, dataParam: Dictionary<string, string>, inNewWindow: boolean, isExternalLink: boolean, actionVerb = "POST"): JQuery<HTMLFormElement>
{
    const data = validateDataParameter(dataParam);
    if (!isExternalLink)
    {
        url = addRequiredQuerystringParams(url, inNewWindow);
        data["sgh"] = sessionGuidHash;
    }

    var form = $("<form>",
        {
            action: url,
            method: actionVerb,
            target: inNewWindow ? "_blank" : "_self"
        }) as JQuery<HTMLFormElement>;

    Object.keys(data).forEach(function (key)
    {
        var input = $("<input>",
            {
                type: "hidden",
                name: key
            });

        if (typeof data[key] === "object")
        {
            input.val(JSON.stringify(data[key]!));
        }
        else
        {
            input.val(data[key]!);
        }
        form.append(input);
    });

    return form;
};

export let submitAndRemoveHiddenInputForm = function (form: HTMLFormElement | JQuery<HTMLFormElement>)
{
    $(document.body).append(form);
    form.submit();
    form.remove();
};

export let deepEquals = function (obj1: object | null | undefined, obj2: object | null | undefined): boolean
{
    if (obj1 === null || obj2 === null)
    {
        return obj1 === obj2;
    }
    if (obj1 === undefined || obj2 === undefined)
    {
        return obj1 === obj2;
    }
    var obj1Keys = Object.keys(obj1);
    var obj2Keys = Object.keys(obj2);

    if (obj1Keys.length !== obj2Keys.length)
    {
        return false;
    }

    var allKeys = obj1Keys.concat(obj2Keys);

    allKeys = allKeys.filter(function (key, i) { return allKeys.indexOf(key) === i; });

    for (var i = 0; i < allKeys.length; i++)
    {
        var key = allKeys[i];

        var firstItem = (obj1 as Dictionary<string, any>)[key];
        var secondItem = (obj2 as Dictionary<string, any>)[key];
        if ((firstItem === undefined || secondItem === undefined) && firstItem !== secondItem)
        {
            return false;
        }

        var typeOfFirstItem = typeof firstItem;
        var typeOfSecondItem = typeof secondItem;

        if (typeOfFirstItem !== typeOfSecondItem)
        {
            return false;
        }

        if (typeOfFirstItem === "object")
        {
            var firstObjectIsEmpty = $.isEmptyObject(firstItem);
            var secondObjectIsEmpty = $.isEmptyObject(secondItem);
            if (!firstObjectIsEmpty && !secondObjectIsEmpty)
            {
                if (!deepEquals(firstItem, secondItem))
                {
                    return false;
                }
            }
            else if (firstObjectIsEmpty !== secondObjectIsEmpty)
            {
                return false;
            }
        }
        else
        {
            if (firstItem !== secondItem)
            {
                return false;
            }
        }
    }

    return true;
};

export let deepDifference = function (
    obj1: object | null | undefined,
    obj2: object | null | undefined
): { areEqual: boolean, differences: string[] }
{
    const differences: string[] = [];

    if (obj1 === null || obj2 === null)
    {
        return { areEqual: obj1 === obj2, differences };
    }
    if (obj1 === undefined || obj2 === undefined)
    {
        return { areEqual: obj1 === obj2, differences };
    }

    const obj1Keys = Object.keys(obj1);
    const obj2Keys = Object.keys(obj2);

    if (obj1Keys.length !== obj2Keys.length)
    {
        differences.push(...obj1Keys.filter(key => !obj2Keys.includes(key)));
        differences.push(...obj2Keys.filter(key => !obj1Keys.includes(key)));
        return { areEqual: false, differences };
    }

    const allKeys = [...new Set([...obj1Keys, ...obj2Keys])];

    for (const key of allKeys)
    {
        const firstItem = (obj1 as Record<string, any>)[key];
        const secondItem = (obj2 as Record<string, any>)[key];

        if ((firstItem === undefined || secondItem === undefined) && firstItem !== secondItem)
        {
            differences.push(key);
            continue;
        }

        const typeOfFirstItem = typeof firstItem;
        const typeOfSecondItem = typeof secondItem;

        if (typeOfFirstItem !== typeOfSecondItem)
        {
            differences.push(key);
            continue;
        }

        if (typeOfFirstItem === "object")
        {
            const firstObjectIsEmpty = $.isEmptyObject(firstItem);
            const secondObjectIsEmpty = $.isEmptyObject(secondItem);
            if (!firstObjectIsEmpty && !secondObjectIsEmpty)
            {
                const result = deepDifference(firstItem, secondItem);
                if (!result.areEqual)
                {
                    differences.push(key);
                    differences.push(...result.differences.map(subKey => `${key}.${subKey}`));
                }
            } 
            else if (firstObjectIsEmpty !== secondObjectIsEmpty)
            {
                differences.push(key);
            }
        } 
        else
        {
            if (firstItem !== secondItem)
            {
                differences.push(key);
            }
        }
    }

    return { areEqual: differences.length === 0, differences };
};


const loadedPartials = new Set<string>();
const loadingPartials: Dictionary<string, JQueryPromise<void>> = {};
const loadedScripts = new Set<string>();
const loadingScripts: Dictionary<string, JQueryPromise<void>> = {};
const loadingNamespaces: Dictionary<string, Promise<unknown>> = {};
let loaded = false;
const pendingModules: Set<Promise<unknown>> = new Set();
export const initialModulesLoaded = (async () =>
{
    await new Promise<void>(res => $(res));
    $("script[src]").each(function ()
    {
        const script = (this as HTMLScriptElement);
        loadedScripts.add(script.src);
        if (script.hasAttribute("data-included-scripts"))
        {
            script.getAttribute("data-included-scripts")?.split(",").forEach(url => loadedScripts.add(window.location.origin + url), loadedScripts);
        }
    });
    $("link[href].xRefStylesheet").each(function ()
    {
        const link = (this as HTMLLinkElement);
        loadedPartials.add(link.href);
    });
    loaded = true;

    while (pendingModules.size)
    {
        await Promise.all(pendingModules);
    }
})();

export function loadSkywardModuleByNamespace(namespace: string)
{
    if (!loadingNamespaces[namespace])
    {
        loadingNamespaces[namespace] = loadModule(namespace).then((data: any) =>
        {
            if (isURL(data))
            {
                loadedScripts.add(data);
                delete loadingNamespaces[namespace];
            }
        });
    }

    return loadingNamespaces[namespace];
}

function isURL(param: any)
{
    try
    {
        new URL(param);
        return true;
    } catch (err)
    {
        return false;
    }
}

/**
 * Asynchronously load scripts from <script> elements
 * @param scripts The scripts to load.  Only scripts with src attributes, and <sky-webpack-module> elements, are supported.
 * @return A promise that will be completed when all scripts are loaded
 */
function loadScripts(scripts: JQuery<HTMLScriptElement>)
{
    const newScripts = scripts.not(function () { return this instanceof HTMLScriptElement && loadedScripts.has(this.src); }).toArray();

    return Promise.all(newScripts.map(function (script)
    {
        const webpackModule = script.dataset["webpackModule"];
        if (webpackModule)
        {
            return loadModule(webpackModule);
        }
        else
        {
            return loadScript(script);
        }
    }));

    function loadScript(script: HTMLScriptElement)
    {
        const existingPromise = loadingScripts[script.src];
        if (existingPromise)
        {
            return existingPromise;
        }

        const scriptLoaded = $.Deferred();
        loadingScripts[script.src] = scriptLoaded.promise();

        // Script elements created via createElement, rather than parsing, will be executed when they are added to the DOM (https://html.spec.whatwg.org/multipage/scripting.html#script-processing-model)
        var newScript = document.createElement("script");
        // Ensure scripts are executed in DOM order
        newScript.async = false;
        for (var i = 0; i < script.attributes.length; i++)
        {
            var attr = script.attributes[i];
            if (attr.namespaceURI === null)
            {
                newScript.setAttribute(attr.name, attr.value);
            }
            else
            {
                newScript.setAttributeNS(attr.namespaceURI, attr.name, attr.value);
            }
        }

        newScript.addEventListener("load", function loaded()
        {
            newScript.removeEventListener("load", loaded);
            loadedScripts.add(script.src);
            scriptLoaded.resolve();
            delete loadingScripts[script.src];
        });

        document.body.appendChild(newScript);
        return scriptLoaded.promise();
    }
};

/**
 * Asynchronously load Partials from <link> elements
 * @param links The links to load. Only links with href attributes are supported.
 * @return A promise that will be completed when all links are loaded
 */
function loadPartials(links: JQuery<HTMLLinkElement>)
{
    const newLinks = links.not(function () { return this instanceof HTMLLinkElement && loadedPartials.has(this.href); }).toArray();

    return Promise.all(newLinks.map(function (link)
    {
        return loadPartial(link);
    }));

    function loadPartial(link: HTMLLinkElement)
    {
        const existingPromise = loadingPartials[link.href];
        if (existingPromise)
        {
            return existingPromise;
        }

        const partialLoaded = $.Deferred();
        loadingPartials[link.href] = partialLoaded.promise();

        // Script elements created via createElement, rather than parsing, will be executed when they are added to the DOM (https://html.spec.whatwg.org/multipage/scripting.html#script-processing-model)
        const newLink = document.createElement("link");
        newLink.rel = "stylesheet";
        for (var i = 0; i < link.attributes.length; i++)
        {
            var attr = link.attributes[i];
            if (attr.namespaceURI === null)
            {
                newLink.setAttribute(attr.name, attr.value);
            }
            else
            {
                newLink.setAttributeNS(attr.namespaceURI, attr.name, attr.value);
            }
        }

        newLink.addEventListener("load", function loaded()
        {
            newLink.removeEventListener("load", loaded);
            loadedPartials.add(link.href);
            partialLoaded.resolve();
            delete loadingPartials[link.href];
        });

        document.head.appendChild(newLink);
        return partialLoaded.promise();
    }
};

/**
 * @description Load a script that was not passed through processViewPartHtml(). Don't call this. It's used by webpack module scripts that are generated on the server.
 */
export function loadViewpartModule(namespace?: string)
{
    if (loaded)
    {
        reportException(new SkywardException(
            "Asynchronous HTML request was not sent using `viewparthtml`. This will lead to slowdowns or errors when loading the page." +
            " To fix this, change the `dataType` parameter in your ajax request to \"viewparthtml\", or pass your html string to skyward.global.processViewPartHtml()."
        ));
    }
    // Protect against DOM clobbering attacks using `<img name="currentScript">`. See https://github.com/advisories/GHSA-4vvj-4cpr-p986
    if (document.currentScript?.tagName.toUpperCase() === 'SCRIPT')
    {
        const modulePromise: Promise<unknown> = loadModule(namespace ?? document.currentScript.dataset["webpackModule"]);

        if (!loaded)
        {
            pendingModules.add(modulePromise);
            modulePromise.then(() =>
            {
                pendingModules.delete(modulePromise);
            });
        }
        return modulePromise;
    }
    reportException(new SkywardException("Could not load module. Element (document.currentScript) is not of type script."));
    return Promise.reject();
}

/**
 * @description Parse the provided HTML, looking for viewpart-specific scripts & partials, and load them. Returns a promise that will be resolved
 * once all scripts & partials are loaded. The promise will be resolved with the parsed HTML, with loaded scripts & partials stripped out.
 * @param html The html to be processed.
 * @returns A promise for the processed html. If there were no scripts or partials in it, the original
 * html string will be returned. The promise will never fail - if loading the scripts or partials fails for some reason, it will resolve with the parsed
 * html.
 */
export async function processViewPartHtml(html: JQuery | HTMLElement | string): Promise<JQuery | string>
{
    // Don't do anything if we don't need to.
    // TODO(2025) re-check how much overhead there is for always separately parsing and inserting html
    if (typeof html === "string" && !/<script /.test(html) && !/<link /.test(html))
    {
        return html;
    }
    const root = $(document.createDocumentFragment());
    root.append(html);
    const links = root.find("link[href].xRefStylesheet") as JQuery<HTMLLinkElement>;
    links.remove();

    const scripts = root.find("script[src],script[data-webpack-module]") as JQuery<HTMLScriptElement>;
    scripts.remove();

    try
    {
        await loadPartials(links)
    }
    catch (e)
    {
        reportException(new SkywardException("Error loading ViewPart partials.",
            JSON.stringify({ exception: e, html: html, scripts: Array.from(scripts).map(script => script.src), links: Array.from(links).map(link => link.href) })));
    }

    try
    {
        await loadScripts(scripts)
    }
    catch (e)
    {
        reportException(new SkywardException("Error loading ViewPart scripts.",
            JSON.stringify({ exception: e, html: html, scripts: Array.from(scripts).map(script => script.src), links: Array.from(links).map(link => link.href) })));
    }

    return root.children();
};

/** 
 * @description Parse the provided HTML, looking for viewpart-specific scripts & partials, and load them, returning the parsed HTML and a promise separately.
 * In most cases using skyward.global.processViewPartHtml is preferable.
 * @param html The html to be processed.
 * @returns The processed HTML, and promises that will be resolved when the viewpart-specific scripts & partials load.
 */
export function processViewPartHtmlSeparately(html: JQuery | HTMLElement | string): { html: string | JQuery, loaded: PromiseLike<any> }
{
    if (typeof html === "string" && !/<script /.test(html) && !/<link /.test(html))
    {
        return { html: html, loaded: $.Deferred().resolve() };
    }

    const root = $(document.createDocumentFragment());
    root.append(html);
    const scripts = root.find("script[src],script[data-webpack-module]");
    scripts.remove();
    const links = root.find("link[href].xRefStylesheet");
    links.remove();

    return { html: root.children(), loaded: Promise.all([loadScripts(scripts as JQuery<HTMLScriptElement>), loadPartials(links as JQuery<HTMLLinkElement>)]) };
};

// Set up the "viewpartHtml" dataType to automatically apply processViewPartHtml().  This can't be done using an ajax converter because they don't resolve promises.
// Based on jQuery's xhr transport: https://github.com/jquery/jquery/blob/cf84696/src/ajax/xhr.js
$.ajaxTransport("viewparthtml", function (options)
{
    // this will populated from xhr.responseText into ajax.data
    (options as any).responseFields["viewparthtml"] = "responseText";
    let xhr: XMLHttpRequest;
    const transport: JQuery.Transport =
    {
        send: function (headers, completeCallback)
        {
            xhr = options.xhr!();
            xhr.open(options.method ?? options.type!, options.url!, options.async!, options.username, options.password);
            if (options.xhrFields)
            {
                $.extend(xhr, options.xhrFields);
            }
            if (options.mimeType)
            {
                xhr.overrideMimeType(options.mimeType);
            }

            headers["Accept"] = "text/plain";
            if (!options.crossDomain && !("X-Requested-With" in headers))
            {
                headers["X-Requested-With"] = "XMLHttpRequest";
            }
            $.each(headers, function (k, v)
            {
                xhr.setRequestHeader(String(k), v);
            });

            xhr.onloadend = async function ()
            {
                // request aborted
                if (xhr.status === 0)
                {
                    return;
                }
                var responseType = xhr.getResponseHeader("content-type");
                if (responseType && responseType.split(";").shift() === "text/html")
                {
                    const processedData = await processViewPartHtml(xhr.responseText);
                    completeCallback(xhr.status, xhr.statusText as JQuery.Ajax.TextStatus, { "viewparthtml": processedData }, xhr.getAllResponseHeaders());
                }
                else
                {
                    completeCallback(xhr.status, xhr.statusText as JQuery.Ajax.TextStatus, { "text": xhr.responseText }, xhr.getAllResponseHeaders());
                }
            };
            xhr.send((options as any).hasContent ? (options.data as XMLHttpRequestBodyInit) : undefined);
        },
        abort: function ()
        {
            if (xhr)
            {
                xhr.abort();
            }
        }
    };
    return transport;
});

var gatherDataForError = function ()
{
    var viewpartScripts = $("script[src]:not([data-packaged])").toArray().map(function (script)
    {
        return (script as HTMLScriptElement).src;
    });

    return {
        viewpartScripts: viewpartScripts,
    };
};

export let isDebug = skyward.global.isDebug || window._skyward?.global?.isDebug || false;
export let virtualDir = skyward.global.virtualDir || window._skyward?.global?.virtualDir || '';
export let sessionGuidHash = skyward.global.sessionGuidHash || window._skyward?.global?.sessionGuidHash || '';
export let windowGuid = skyward.global.windowGuid || window._skyward?.global?.windowGuid || '';
export let windowEntityID = skyward.global.windowEntityID || window._skyward?.global?.windowEntityID || 0;
export let windowDistrictID = skyward.global.windowDistrictID || window._skyward?.global?.windowDistrictID || 0;
export let windowSchoolYearID = skyward.global.windowSchoolYearID || window._skyward?.global?.windowSchoolYearID || 0;
export let windowFiscalYearID = skyward.global.windowFiscalYearID || window._skyward?.global?.windowFiscalYearID || 0;
export let pageStateGuid = skyward.global.pageStateGuid || window._skyward?.global?.pageStateGuid || '';
export let loggedIn = skyward.global.loggedIn || window._skyward?.global?.loggedIn || false;
export let userIdHash = skyward.global.userIdHash || window._skyward?.global?.userIdHash || '';
export let isSuperUser = skyward.global.isSuperUser ?? window._skyward?.global?.isSuperUser ?? false;
export let isRealSuperUser = skyward.global.isRealSuperUser ?? window._skyward?.global?.isRealSuperUser ?? false;
export let isSupportAccount = skyward.global.isSupportAccount ?? window._skyward?.global?.isSupportAccount ?? false;
export let isRealSupportAccount = skyward.global.isRealSupportAccount ?? window._skyward?.global?.isRealSupportAccount ?? false;
export let isInHouse = skyward.global.isInHouse ?? window._skyward?.global?.isInHouse ?? false;
export let sessionTimeoutSeconds = skyward.global.sessionTimeoutSeconds || '';
export let mobileAccess = skyward.global.mobileAccess || window._skyward?.global?.mobileAccess || false;
export let mobileAccessEmulation = skyward.global.mobileAccessEmulation || window._skyward?.global?.mobileAccessEmulation || false;
export let isAndroidApp = skyward.global.isAndroidApp || window._skyward?.global?.isAndroidApp || false;
export let isIosApp = skyward.global.isIosApp || window._skyward?.global?.isIosApp || false;
export let isReadOnlyImpersonation = skyward.global.isReadOnlyImpersonation || window._skyward?.global?.isReadOnlyImpersonation || false;
export let froalaLicenseKey = skyward.global.froalaLicenseKey || window._skyward?.global?.froalaLicenseKey || '';
export let sessionWarnSeconds = skyward.global.sessionWarnSeconds || '';
export let sessionClientPingSeconds = skyward.global.sessionClientPingSeconds || '';

// Keep in sync with 66A0C793-0560-4F5C-B6B2-24B5586FDBC8 references
export const readOnlyAnnouncementID = "ReadOnlyScreenReaderAnnouncement";

export let getPrefersReducedMotion = lazy(() => window.matchMedia(`(prefers-reduced-motion: reduce)`).matches || false);
Object.defineProperty(skyward.global, "prefersReducedMotion",
{
    get: getPrefersReducedMotion,
    configurable: true,
    enumerable: true,
});

export let addRequiredQuerystringParams = function (uri: string, inNewWindow?: boolean)
{
    var requiredParams: Dictionary<string, string> =
    {
        w: windowGuid,
        p: pageStateGuid 
    };

    if (inNewWindow)
    {
        requiredParams.generateNewDefaultWindow = "true";
    }

    Object.keys(requiredParams).forEach(function (param)
    {
        if (requiredParams[param]!.length)
        {
            uri = setQueryStringParameter(uri, param, requiredParams[param]!);
        }
    });

    return uri;

    function setQueryStringParameter(uri: string, key: string, value: string)
    {
        var re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
        var separator = uri.indexOf('?') !== -1 ? "&" : "?";
        if (uri.match(re))
        {
            return uri.replace(re, '$1' + key + "=" + value + '$2');
        }
        else
        {
            return uri + separator + key + "=" + value;
        }
    }
};

const dayjs = require("dayjs");
const utc = require("dayjs/plugin/utc");
const isSameOrAfter = require("dayjs/plugin/isSameOrAfter");
const advancedFormat = require("dayjs/plugin/advancedFormat")

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(advancedFormat);

(window as any).dayjs = dayjs;

registerErrorDataGatherer("global", gatherDataForError);
